import React, { useState, useEffect } from "react";
import { MdSearchOff } from "react-icons/md";
import InputCheckbox from "../inputs/inputCheckbox/inputCheckbox";
import Pagination from "../pagination/pagination";
import OptionsMenu from "../optionsMenu/optionsMenu";

import "./table.scss";

const Table = ({
    columns,
    data,
    actions,
    noResultsMessage,
    showHeaderCheckbox = true,
    onRowSelect,
    currentPage: externalCurrentPage,
    onPageChange: externalOnPageChange,
    resetPaginationOnDataChange = true,
}) => {
    const [internalCurrentPage, setInternalCurrentPage] = useState(1);
    
    // Usar paginación externa si se proporciona, sino usar interna
    const currentPage = externalCurrentPage !== undefined ? externalCurrentPage : internalCurrentPage;
    const onPageChange = externalOnPageChange !== undefined ? externalOnPageChange : setInternalCurrentPage;
    
    useEffect(() => {
        if (resetPaginationOnDataChange && externalCurrentPage === undefined) {
            setInternalCurrentPage(1);
        }
    }, [data, resetPaginationOnDataChange, externalCurrentPage]);
    const [selectedRows, setSelectedRows] = useState([]);
    const rowsPerPage = 7;

    const [originalData] = useState(data);
    const [sortConfig, setSortConfig] = useState({
        key: null,
        direction: null,
    });

    const indexOfLastRow = currentPage * rowsPerPage;
    const indexOfFirstRow = indexOfLastRow - rowsPerPage;

    // Ordenar los datos según la configuración
    const sortedData = React.useMemo(() => {
        let sortableData = [...data];
        if (sortConfig.key) {
            sortableData.sort((a, b) => {
                if (a[sortConfig.key] < b[sortConfig.key]) {
                    return sortConfig.direction === "ascending" ? -1 : 1;
                }
                if (a[sortConfig.key] > b[sortConfig.key]) {
                    return sortConfig.direction === "ascending" ? 1 : -1;
                }
                return 0;
            });
        }
        return sortableData;
    }, [data, sortConfig]);

    // Obtener las filas actuales
    const currentRows = sortedData.slice(indexOfFirstRow, indexOfLastRow);
    const totalPages = Math.ceil(data.length / rowsPerPage);

    const handleSelectRow = (rowId) => {
        setSelectedRows((prevSelectedRows) => {
            const newSelectedRows = prevSelectedRows.includes(rowId)
                ? prevSelectedRows.filter((id) => id !== rowId)
                : [...prevSelectedRows, rowId];

            console.log("Selected rows actualizados:", newSelectedRows);

            if (onRowSelect) {
                onRowSelect(newSelectedRows);
            }

            return newSelectedRows;
        });
    };

    const handleSelectAll = (e) => {
        const isChecked = e.target.checked;
        if (isChecked) {
            const allRowIds = data.map((row, index) => 
                row.codTarifaMc || row.CodTarifaMc || row.codMueble || row.idImportacion || row.IdImportacion || row.Id || row.id || index
            );
            setSelectedRows(allRowIds);
            if (onRowSelect) {
                onRowSelect(allRowIds);
            }
        } else {
            setSelectedRows([]);
            if (onRowSelect) {
                onRowSelect([]);
            }
        }
    };

    const requestSort = (key) => {
        let direction = "ascending";
        if (sortConfig.key === key) {
            direction =
                sortConfig.direction === "ascending" ? "descending" : null;
        }
        setSortConfig({ key: direction ? key : null, direction });
    };

    console.log("🔍 DEBUG - Table recibió columns:", columns);
    console.log("🔍 DEBUG - Table recibió data:", data);

    return (
        <div className="table-container">
            <table className="custom-table">
                <thead>
                    <tr>
                        {showHeaderCheckbox && (
                            <th>
                                <InputCheckbox
                                    checked={
                                        selectedRows.length === data.length &&
                                        data.length > 0
                                    }
                                    onChange={handleSelectAll}
                                />
                            </th>
                        )}
                        {columns.map((col) => (
                            <th
                                key={col.key}
                                onClick={() => requestSort(col.key)}
                                className={col.className || ""}
                                style={{ 
                                    width: col.width || 'auto',
                                    minWidth: col.width || 'auto'
                                }}
                            >
                                {col.label}
                                {sortConfig.key === col.key
                                    ? sortConfig.direction === "ascending"
                                        ? " ▲"
                                        : " ▼"
                                    : null}
                            </th>
                        ))}
                        {actions && <th>Acciones</th>}
                    </tr>
                </thead>
                <tbody>
                    {currentRows.length > 0 ? (
                        currentRows.map((row, index) => (
                            <tr key={row.codTarifaMc || row.CodTarifaMc || row.codMueble || row.idImportacion || row.IdImportacion || row.Id || row.id || index}>
                                {showHeaderCheckbox && (
                                    <td>
                                        <InputCheckbox
                                            checked={selectedRows.includes(
                                                row.codTarifaMc || row.CodTarifaMc || row.codMueble || row.idImportacion || row.IdImportacion || row.Id || row.id || index
                                            )}
                                            onChange={() =>
                                                handleSelectRow(row.codTarifaMc || row.CodTarifaMc || row.codMueble || row.idImportacion || row.IdImportacion || row.Id || row.id || index)
                                            }
                                        />
                                    </td>
                                )}
                                {columns.map((col) => (
                                    <td
                                        key={`${row.codTarifaMc || row.CodTarifaMc || row.codMueble || row.idImportacion || row.IdImportacion || row.Id || row.id || index}-${col.key}`}
                                        title={row[col.key]}
                                        className={col.className || ""}
                                        style={{ 
                                            width: col.width || 'auto',
                                            minWidth: col.width || 'auto'
                                        }}
                                    >
                                        {col.render ? col.render(row) : row[col.key]}
                                    </td>
                                ))}
                                {actions && (
                                    <td>
                                        <OptionsMenu options={actions(row)} />
                                    </td>
                                )}
                            </tr>
                        ))
                    ) : (
                        <tr>
                            <td
                                colSpan={columns.length + (showHeaderCheckbox ? 1 : 0) + (actions ? 1 : 0)}
                                className="no-results-cell"
                            >
                                <div className="no-results-cell__content">
                                    <MdSearchOff className="no-results-cell__content__icon" />
                                    <p className="no-results-cell__content__text">
                                        {noResultsMessage ||
                                            "No se han encontrado resultados."}
                                    </p>
                                </div>
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
            {totalPages > 1 && (
                <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={onPageChange}
                />
            )}
        </div>
    );
};

export default Table;
